// Pure geometry for the glasses map prototype. No DOM, no React, so it can be
// unit-tested on its own and reused by the Phaser client later.

export interface LatLng {
  lat: number;
  lng: number;
}

export interface FramePoint {
  x: number;
  y: number;
}

/** Mean Earth radius in metres (IUGG). */
export const EARTH_RADIUS_M = 6371008.8;

const DEG = Math.PI / 180;

export function toRadians(deg: number): number {
  return deg * DEG;
}

export function toDegrees(rad: number): number {
  return rad / DEG;
}

/** Normalise any angle in degrees into [0, 360). */
export function normalizeDegrees(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/** Great-circle distance in metres (haversine). */
export function distanceM(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const la1 = toRadians(a.lat);
  const la2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial compass bearing from a to b, degrees clockwise from true north, [0, 360). */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const la1 = toRadians(a.lat);
  const la2 = toRadians(b.lat);
  const dLng = toRadians(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLng);
  return normalizeDegrees(toDegrees(Math.atan2(y, x)));
}

/**
 * Angle the arrow should point on screen, given where the target is (bearing)
 * and where the player is facing (heading). 0 means straight ahead, positive
 * means turn right, negative means turn left. Result is in (-180, 180].
 */
export function relativeAngleDeg(bearing: number, heading: number): number {
  let d = normalizeDegrees(bearing - heading);
  if (d > 180) d -= 360;
  return d;
}

/**
 * Bearing of travel from two successive fixes, or null when the player has
 * not moved far enough for the bearing to mean anything (GPS jitter is a few
 * metres, so anything under `minMoveM` is noise).
 */
export function bearingFromFixes(prev: LatLng, next: LatLng, minMoveM = 6): number | null {
  if (distanceM(prev, next) < minMoveM) return null;
  return bearingDeg(prev, next);
}

/** Local east/north offset in metres of `point` from `origin` (equirectangular; fine under a few km). */
export function localOffsetM(origin: LatLng, point: LatLng): { east: number; north: number } {
  const east = toRadians(point.lng - origin.lng) * Math.cos(toRadians(origin.lat)) * EARTH_RADIUS_M;
  const north = toRadians(point.lat - origin.lat) * EARTH_RADIUS_M;
  return { east, north };
}

export interface Projection {
  /** Where the origin (the player) sits in the frame, in pixels. */
  centre: FramePoint;
  /** Scale: how many metres one pixel covers. */
  metresPerPx: number;
  /** Direction that points straight up on screen, degrees from true north. 0 means north-up. */
  upHeadingDeg: number;
}

/**
 * Project a world point into the 600x600 frame. With `upHeadingDeg` equal to
 * the player's heading the map is heading-up: what is ahead of the player is
 * above the player dot.
 */
export function projectToFrame(origin: LatLng, point: LatLng, p: Projection): FramePoint {
  const { east, north } = localOffsetM(origin, point);
  const h = toRadians(p.upHeadingDeg);
  // Rotate the world so that `upHeadingDeg` becomes screen-north.
  const rx = east * Math.cos(h) - north * Math.sin(h);
  const ry = east * Math.sin(h) + north * Math.cos(h);
  return {
    x: p.centre.x + rx / p.metresPerPx,
    y: p.centre.y - ry / p.metresPerPx,
  };
}

/**
 * Pick a scale so that a target `distance` metres away lands `fitRadiusPx`
 * pixels from the player dot, clamped so the map neither zooms into GPS noise
 * nor out to a whole city.
 */
export function autoMetresPerPx(
  distance: number,
  fitRadiusPx: number,
  minMetresPerPx = 0.25,
  maxMetresPerPx = 8
): number {
  if (!(distance > 0) || !(fitRadiusPx > 0)) return minMetresPerPx;
  return Math.min(maxMetresPerPx, Math.max(minMetresPerPx, distance / fitRadiusPx));
}

/** Short, glanceable distance text. Metres under 1 km, else kilometres; feet under about a quarter mile, else miles. */
export function formatDistance(metres: number, units: 'm' | 'ft' = 'm'): string {
  if (!Number.isFinite(metres) || metres < 0) return '';
  if (units === 'ft') {
    const feet = metres * 3.28084;
    if (feet < 1000) return `${Math.round(feet / 10) * 10} ft`;
    const miles = feet / 5280;
    return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
  }
  if (metres < 1000) return `${Math.round(metres / 5) * 5} m`;
  const km = metres / 1000;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
}

/**
 * Split `formatDistance`'s output into its number and its unit, so a hero
 * display can render the number large and the unit smaller. `formatDistance`
 * always returns "<number> <unit>", so this just splits on the last space.
 */
export function splitDistance(metres: number, units: 'm' | 'ft' = 'm'): [string, string] {
  const text = formatDistance(metres, units);
  const i = text.lastIndexOf(' ');
  return i === -1 ? [text, ''] : [text.slice(0, i), text.slice(i + 1)];
}

/** Turn a bearing into a compass point label the player can say back: N, NE, E, ... */
export function compassPoint(bearing: number): string {
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return points[Math.round(normalizeDegrees(bearing) / 45) % 8];
}
